import React, { useEffect, useState } from 'react';
import {
    ArrowDownLeft, ArrowUpRight, CalendarDays, CheckCircle2, Clock3,
    Landmark, RefreshCw, Search, Smartphone, Users, WalletCards, X
} from 'lucide-react';
import Layout from '../components/Layout';
import ResilientImage from '../components/ResilientImage';
import socketService from '../services/socketService';
import './Dashboard.css';

const emptyFlow = { totalWithdraw: 0, totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 };

const Dashboard = () => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalDevices: 0,
        today: { ...emptyFlow },
        yesterday: { ...emptyFlow },
        lifetime: { ...emptyFlow },
        lastMonth: { ...emptyFlow },
        currentMonth: { ...emptyFlow },
        depositToday: { ...emptyFlow },
        depositYesterday: { ...emptyFlow },
        depositLifetime: { ...emptyFlow },
        depositLastMonth: { ...emptyFlow },
        depositCurrentMonth: { ...emptyFlow },
        customWithdraw: { ...emptyFlow },
        customDeposit: { ...emptyFlow },
        loading: true,
        error: null
    });

    const adminId = localStorage.getItem('adminId');
    const money = (value) => `৳${Number(value || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const count = (value) => Number(value || 0).toLocaleString('en-BD');

    const getCommonPayload = () => {
        let allowedUsers = [];
        try {
            allowedUsers = JSON.parse(localStorage.getItem('allowedUsers') || '[]');
        } catch (error) {
            allowedUsers = [];
        }
        return {
            isMaster: localStorage.getItem('isMaster'),
            masterKey: localStorage.getItem('masterKey'),
            allowedUsers
        };
    };

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        let isMounted = true;
        let retryCount = 0;

        const requestData = () => {
            if (!adminId) {
                if (isMounted) setStats((previous) => ({ ...previous, loading: false, error: 'Admin session was not found.' }));
                return;
            }
            socketService.emit('register-admin-socket', { adminId });
            setTimeout(() => socketService.emit('fetch-dashboard-data', getCommonPayload()), 300);
        };

        const handleMainData = (data) => {
            if (!isMounted) return;
            setStats((previous) => ({
                ...previous,
                totalUsers: data.total_users || 0,
                totalDevices: data.total_devices || 0,
                today: data.today || previous.today,
                yesterday: data.yesterday || previous.yesterday,
                lifetime: data.lifetime || previous.lifetime,
                lastMonth: data.lastMonth || previous.lastMonth,
                currentMonth: data.currentMonth || previous.currentMonth,
                depositToday: data.depositToday || previous.depositToday,
                depositYesterday: data.depositYesterday || previous.depositYesterday,
                depositLifetime: data.depositLifetime || previous.depositLifetime,
                depositLastMonth: data.depositLastMonth || previous.depositLastMonth,
                depositCurrentMonth: data.depositCurrentMonth || previous.depositCurrentMonth,
                loading: false,
                error: null
            }));
            retryCount = 0;
        };

        const handleFilteredData = (data) => {
            if (!isMounted) return;
            setStats((previous) => ({
                ...previous,
                customWithdraw: data.customWithdraw || { ...emptyFlow },
                customDeposit: data.customDeposit || { ...emptyFlow }
            }));
            setIsFilterLoading(false);
        };

        const handleError = (message) => {
            if (!isMounted) return;
            if (message?.toLowerCase().includes('unauthorized') && retryCount < 3) {
                retryCount += 1;
                requestData();
                return;
            }
            setStats((previous) => ({ ...previous, loading: false, error: message || 'Unable to load dashboard data.' }));
            setIsFilterLoading(false);
        };

        socketService.on('dashboard-data-response', handleMainData);
        socketService.on('filtered-data-response', handleFilteredData);
        socketService.on('dashboard-error', handleError);
        socketService.on('connect', requestData);
        if (typeof socketService.connect === 'function') socketService.connect();
        requestData();

        return () => {
            isMounted = false;
            socketService.off('dashboard-data-response', handleMainData);
            socketService.off('filtered-data-response', handleFilteredData);
            socketService.off('dashboard-error', handleError);
            socketService.off('connect', requestData);
        };
    }, [adminId]);

    const handleSearch = () => {
        if (!selectedDate) return;
        setAppliedDate(selectedDate);
        setIsFilterLoading(true);
        socketService.emit('fetch-filtered-data', { ...getCommonPayload(), customDate: selectedDate });
    };

    const clearFilter = () => {
        setSelectedDate('');
        setAppliedDate('');
        setStats((previous) => ({ ...previous, customWithdraw: { ...emptyFlow }, customDeposit: { ...emptyFlow } }));
    };

    const periods = [
        { label: 'Today', deposit: stats.depositToday, withdraw: stats.today },
        { label: 'Yesterday', deposit: stats.depositYesterday, withdraw: stats.yesterday },
        { label: 'Running month', deposit: stats.depositCurrentMonth, withdraw: stats.currentMonth },
        { label: 'Last month', deposit: stats.depositLastMonth, withdraw: stats.lastMonth },
        { label: 'Lifetime', deposit: stats.depositLifetime, withdraw: stats.lifetime }
    ];

    if (stats.loading) {
        return <Layout><div className="gp-dashboard-state"><RefreshCw className="gp-spin" size={28} /><strong>Loading payment operations…</strong></div></Layout>;
    }

    if (stats.error) {
        return <Layout><div className="gp-dashboard-state error"><strong>Dashboard unavailable</strong><span>{stats.error}</span></div></Layout>;
    }

    return (
        <Layout>
            <div className="gp-dashboard">
                <section className="gp-dashboard-heading">
                    <div>
                        <span className="gp-eyebrow">Payment operations</span>
                        <h1>Financial overview</h1>
                        <p>Monitor verified deposits, completed withdrawals and connected accounts.</p>
                    </div>
                    <div className="gp-dashboard-clock">
                        <Clock3 size={18} />
                        <div>
                            <strong>{currentTime.toLocaleTimeString('en-US', { timeZone: 'Asia/Dhaka', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</strong>
                            <small>{currentTime.toLocaleDateString('en-GB', { timeZone: 'Asia/Dhaka', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })} · Bangladesh time</small>
                        </div>
                    </div>
                </section>

                <section className="gp-kpi-grid">
                    <article className="gp-kpi-card primary">
                        <span className="gp-kpi-icon"><ArrowDownLeft size={21} /></span>
                        <div><small>Today's verified deposits</small><strong>{money(stats.depositToday.totalDeposit)}</strong><span>Bkash {money(stats.depositToday.bkashTotal)} · Nagad {money(stats.depositToday.nagadTotal)}</span></div>
                    </article>
                    <article className="gp-kpi-card dark">
                        <span className="gp-kpi-icon"><ArrowUpRight size={21} /></span>
                        <div><small>Today's completed withdrawals</small><strong>{money(stats.today.totalWithdraw)}</strong><span>Bkash {money(stats.today.bkashTotal)} · Nagad {money(stats.today.nagadTotal)}</span></div>
                    </article>
                    <article className="gp-kpi-card">
                        <span className="gp-kpi-icon"><Users size={21} /></span>
                        <div><small>Registered users</small><strong>{count(stats.totalUsers)}</strong><span>Gateway customer accounts</span></div>
                    </article>
                    <article className="gp-kpi-card">
                        <span className="gp-kpi-icon"><Smartphone size={21} /></span>
                        <div><small>Registered devices</small><strong>{count(stats.totalDevices)}</strong><span>Payment worker devices</span></div>
                    </article>
                </section>

                <section className="gp-date-filter">
                    <div className="gp-section-copy"><span className="gp-section-icon"><CalendarDays size={19} /></span><div><h2>Date reconciliation</h2><p>Review verified payment totals for a specific Bangladesh date.</p></div></div>
                    <div className="gp-date-actions">
                        <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                        <button type="button" className="gp-primary-button" onClick={handleSearch} disabled={!selectedDate || isFilterLoading}>{isFilterLoading ? <RefreshCw className="gp-spin" size={16} /> : <Search size={16} />} View totals</button>
                    </div>
                </section>

                {appliedDate && (
                    <section className="gp-filter-result">
                        <div className="gp-filter-result-head"><div><span>Reconciliation date</span><strong>{new Date(`${appliedDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></div><button type="button" onClick={clearFilter}><X size={15} /> Clear</button></div>
                        <div className="gp-filter-result-grid">
                            <div><span>Verified deposits</span><strong>{money(stats.customDeposit.totalDeposit)}</strong><small>Bkash {money(stats.customDeposit.bkashTotal)} · Nagad {money(stats.customDeposit.nagadTotal)}</small></div>
                            <div><span>Completed withdrawals</span><strong>{money(stats.customWithdraw.totalWithdraw)}</strong><small>Bkash {money(stats.customWithdraw.bkashTotal)} · Nagad {money(stats.customWithdraw.nagadTotal)}</small></div>
                            <div><span>Net flow</span><strong className={(stats.customDeposit.totalDeposit - stats.customWithdraw.totalWithdraw) < 0 ? 'negative' : ''}>{money(stats.customDeposit.totalDeposit - stats.customWithdraw.totalWithdraw)}</strong><small>Deposit minus withdrawal</small></div>
                        </div>
                    </section>
                )}

                <section className="gp-report-card">
                    <div className="gp-report-head"><div><span className="gp-section-icon"><WalletCards size={19} /></span><div><h2>Transaction summary</h2><p>Verified value grouped by reporting period.</p></div></div><span className="gp-verified-pill"><CheckCircle2 size={14} /> Verified records only</span></div>
                    <div className="gp-report-table-wrap">
                        <table className="gp-report-table">
                            <thead><tr><th>Period</th><th>Deposits</th><th>Withdrawals</th><th>Net flow</th><th>Bkash deposit</th><th>Nagad deposit</th></tr></thead>
                            <tbody>{periods.map((period) => {
                                const net = Number(period.deposit.totalDeposit || 0) - Number(period.withdraw.totalWithdraw || 0);
                                return <tr key={period.label}><td><strong>{period.label}</strong></td><td className="positive">{money(period.deposit.totalDeposit)}</td><td>{money(period.withdraw.totalWithdraw)}</td><td className={net < 0 ? 'negative' : 'positive'}>{money(net)}</td><td>{money(period.deposit.bkashTotal)}</td><td>{money(period.deposit.nagadTotal)}</td></tr>;
                            })}</tbody>
                        </table>
                    </div>
                </section>

                <section className="gp-provider-grid">
                    <article className="gp-provider-card"><div className="gp-provider-head"><ResilientImage src="/bkash.jpg" alt="Bkash" /><div><h3>Bkash settlement</h3><span>Lifetime processed value</span></div></div><strong>{money(stats.depositLifetime.bkashTotal + stats.lifetime.bkashTotal)}</strong><div><span><ArrowDownLeft size={14} /> Deposits {money(stats.depositLifetime.bkashTotal)}</span><span><ArrowUpRight size={14} /> Withdrawals {money(stats.lifetime.bkashTotal)}</span></div></article>
                    <article className="gp-provider-card"><div className="gp-provider-head"><ResilientImage src="/nagad.png" alt="Nagad" /><div><h3>Nagad settlement</h3><span>Lifetime processed value</span></div></div><strong>{money(stats.depositLifetime.nagadTotal + stats.lifetime.nagadTotal)}</strong><div><span><ArrowDownLeft size={14} /> Deposits {money(stats.depositLifetime.nagadTotal)}</span><span><ArrowUpRight size={14} /> Withdrawals {money(stats.lifetime.nagadTotal)}</span></div></article>
                    <article className="gp-provider-card neutral"><div className="gp-provider-head"><span className="gp-provider-mark"><Landmark size={22} /></span><div><h3>Gateway throughput</h3><span>All supported payment channels</span></div></div><strong>{money(stats.depositLifetime.totalDeposit + stats.lifetime.totalWithdraw)}</strong><div><span>Total deposit {money(stats.depositLifetime.totalDeposit)}</span><span>Total withdraw {money(stats.lifetime.totalWithdraw)}</span></div></article>
                </section>
            </div>
        </Layout>
    );
};

export default Dashboard;
